import React, { ReactElement } from 'react';
import { motion } from 'framer-motion';

import COLORS from '~/constants/colors';

import { RendererProps } from '../../models';
import { INPUT_MODES } from '../../../../../../constants/defaults';
import { getPx } from '../../helpers';

function Receptor({ entity }: RendererProps): ReactElement {
  const pos = entity.getPos();
  const jogging = entity.inputMode === INPUT_MODES.jog;

  // Styling
  const noteB = entity.noteDims.b ?? 0;
  const receptorStyle = {
    position: 'absolute' as const,
    width: getPx(entity.dims.w),
    height: getPx(entity.dims.h),
    left: getPx(pos.x - entity.dims.w / 2),
    top: getPx(pos.y - entity.dims.h / 2),
    textAlign: 'center' as const,
    zIndex: 3,
  };
  const getRGBA = (c) => {
    const matches = c.match(/\w\w/g) ?? [];
    const [r, g, b, a] = matches.map((v) => parseInt(v, 16));

    return `rgba(${r}, ${g}, ${b}, ${a ? a / 255 : 1})`;
  };
  const getFrame = (c, w = entity.landingB, s = entity.noteDims.w / 4) =>
    `0 0 0 ${getPx(w)} ${getRGBA(c)}, 0 0 ${getPx(s)} 0 ${getRGBA(
      COLORS.black50
    )}`;
  const resetFrame = getFrame(COLORS.white);
  const panelB = entity.panelDims.b ?? 0;
  const panelM = entity.panelDims.m ?? 0;
  const sideMargins =
    entity.panelDims.w / 2 + panelB + panelM - entity.noteDims.w / 2;
  const landingStyle = {
    display: 'inline-block',
    width: getPx(entity.noteDims.w),
    height: getPx(entity.noteDims.h),
    margin: `${getPx(entity.landingB)} ${getPx(sideMargins)} 0`,
    borderRadius: getPx(3 * noteB),
    backgroundColor: COLORS.black50,
    boxShadow: resetFrame,
  };

  // Animation
  const variants = {
    reset: {
      x: 0,
      boxShadow: resetFrame,
    },
    wrong: {
      x: [0, -2.5, 2.5, 0],
      boxShadow: [getFrame(COLORS.mandy), resetFrame],
      transition: { duration: 0.3 },
    },
    combo: {
      boxShadow: [
        getFrame(COLORS.pastelGreen, 1.69 * entity.landingB),
        resetFrame,
      ],
      transition: { duration: 0.3 },
    },
    special: {
      boxShadow: [
        getFrame(COLORS.mandy),
        getFrame(COLORS.jaffa, 1.23 * entity.landingB),
        getFrame(COLORS.kournikova, 1.46 * entity.landingB),
        getFrame(COLORS.wildWillow, 1.69 * entity.landingB),
        getFrame(COLORS.malibu, 1.46 * entity.landingB),
        getFrame(COLORS.lilacBush, 1.26 * entity.landingB),
        getFrame(COLORS.mandy),
        resetFrame,
      ],
      transition: { duration: 0.6, ease: 'easeIn' },
    },
  };

  return (
    <div style={receptorStyle}>
      {!jogging && (
        <motion.div
          initial={false}
          animate={entity.animates[0]}
          variants={variants}
          style={landingStyle}
        />
      )}
      <motion.div
        initial={false}
        animate={entity.animates[1]}
        variants={variants}
        style={landingStyle}
      />
      {!jogging && (
        <motion.div
          initial={false}
          animate={entity.animates[2]}
          variants={variants}
          style={landingStyle}
        />
      )}
    </div>
  );
}

export default Receptor;
